import type { TranslationTree } from '../../types';

export const toolRecommender: TranslationTree = {
  toolRecommender: {
    page: {
      breadcrumb: 'Werkzeug-Ökodesign',
      badge: 'Genetischer Algorithmus-Optimierer',
      title: 'Das am besten geeignete Werkzeug für Ihren Bearbeitungskontext finden',
      description:
        'Konfigurieren Sie Ihre Bearbeitungsrandbedingungen und starten Sie den genetischen Algorithmus. Das Modell bewertet Werkzeugkandidaten und gibt sie nach vorhergesagter Leistung sortiert zurück.',
    },
    tabs: {
      generate: 'Generieren',
      history: 'Verlauf',
    },
    form: {
      sectionTitle: 'Konfiguration',
      descriptionLabel: 'Laufbeschreibung (optional)',
      descriptionPlaceholder: 'z.B. Aluminium-Schlichtbearbeitung',
      materialsLabel: 'Werkstoffe',
      operationsLabel: 'Bearbeitungsoperationen',
      nJobsLabel: 'Parallele Threads (nJobs)',
      topNLabel: 'Ergebnisse (topN)',
      baseToolLabel: 'Basiswerkzeug (optional)',
      baseToolPlaceholder: '{"d1": 1, "Nb dents": 3, ...}',
      runButton: 'GA starten',
    },
    running: {
      title: 'GA läuft…',
      hint: 'Dies kann mehrere Minuten dauern (bis zu 5). Bitte warten.',
      resumedHint:
        'Sie haben die Seite verlassen und sind zurückgekehrt — das Experiment läuft noch im Hintergrund.',
    },
    results: {
      redirect: 'Experiment abgeschlossen — Ergebnisse werden geöffnet…',
    },
    timeout: {
      title: 'GA noch aktiv',
      message:
        'Der Algorithmus hat das Antwort-Timeout überschritten. Ergebnisse erscheinen im Verlauf nach Abschluss.',
      historyHint:
        'Wechseln Sie zur Registerkarte Verlauf und aktualisieren Sie, um das Ergebnis zu sehen, sobald es bereit ist.',
    },
    idle: {
      title: 'Noch keine Ergebnisse',
      description:
        'Konfigurieren Sie die Randbedingungen und starten Sie den GA, um Werkzeugkandidaten zu sehen.',
    },
    error: {
      title: 'Inferenz fehlgeschlagen',
    },
    history: {
      sectionTitle: 'Vergangene Experimente',
      empty: 'Noch keine Experimente. Starten Sie den GA, um Ergebnisse zu sehen.',
      colDate: 'Datum',
      colDescription: 'Beschreibung',
      colId: 'Experiment-ID',
      colResults: 'Ergebnisse',
      viewLink: 'Ansehen',
      refresh: 'Verlauf aktualisieren',
      results: '{{n}} Ergebnisse',
    },
    detail: {
      breadcrumb: 'Experiment',
      noResults: 'Keine Ergebnisse für dieses Experiment gespeichert.',
      resultLabel: 'Ergebnis',
      baseScoreLabel: 'Bewertung Basiswerkzeug',
      generatedScoreLabel: 'Bewertung generiertes Werkzeug',
      baseFromCatalog: 'Basiswerkzeug: Katalogartikel',
      baseFromUser: 'Basiswerkzeug: Ihr Werkzeug',
      noBaseTool: 'Kein Basiswerkzeug für den Vergleich verfügbar.',
      colField: 'Feld',
      colBase: 'Basiswerkzeug',
      colGenerated: 'Generiertes Werkzeug',
      advancedTitle: 'Erweitert — {{n}} weitere Felder des Basiswerkzeugs',
      exportXlsx: 'Als Excel exportieren',
      exportRank: 'Rang',
      exportMaterial: 'Werkstoff',
      exportOperation: 'Operation',
      exportBaseToolArticle: 'Artikel Basiswerkzeug',
    },
    baseTool: {
      sheetTitle: 'Basiswerkzeug',
      sheetDescription:
        'Beschreiben Sie das zu optimierende Werkzeug. Das Formular startet mit dem häufigsten Werkzeug des Katalogs; alle Felder sind Pflichtfelder, und im Reiter JSON lässt sich ein bestehendes Werkzeug einfügen.',
      tabForm: 'Formular',
      tabJson: 'JSON',
      filled: '{{filled}} / {{total}} Felder ausgefüllt',
      errors: '{{n}} ungültige(r) Wert(e)',
      choose: 'Auswählen…',
      range: '{{min}} bis {{max}} {{unit}}',
      rangeExclusive: '> {{min}} bis {{max}} {{unit}}',
      clear: 'Alles löschen',
      restoreDefaults: 'Standardwerte',
      apply: 'Dieses Werkzeug verwenden',
      jsonLabel: 'Werkzeug-JSON — die Schlüssel sind die ISO-13399-Aliasse',
      groups: {
        dimensions: 'Abmessungen',
        cutting: 'Schneide',
        angles: 'Winkel und Freiwinkel',
        grinding: 'Anschliff, Kern und Spannuten',
        material: 'Werkzeugwerkstoff',
      },
      issues: {
        outOfRange: 'Außerhalb des Bereichs: {{range}}',
        notANumber: 'Zahl erwartet',
        unknownChoice: 'Unbekannter Wert',
      },
      json: {
        syntax: 'Ungültiges JSON: {{message}}',
        notAnObject: 'Ein JSON-Objekt wird erwartet, zum Beispiel {"d1": 3}',
        unknownKeys: 'Dem Modell unbekannte Schlüssel, sie werden ignoriert: {{keys}}',
      },
      summary: {
        none: 'Kein Basiswerkzeug',
        noneHint: 'Der Algorithmus startet mit den besten Katalogwerkzeugen.',
        line: 'Ø{{d1}} · {{teeth}} Zähne · Drall {{helix}}° · {{substrate}}',
        setHint: 'Dient als Ausgangspunkt des Algorithmus.',
        define: 'Werkzeug definieren…',
        edit: 'Bearbeiten…',
        remove: 'Entfernen',
      },
    },
    fields: {
      d1: 'Schneidendurchmesser (d1)',
      l1: 'Schneidenlänge (l1)',
      nbDents: 'Zähnezahl',
      helice1: 'Drallwinkel 1 [°]',
      helice2: 'Drallwinkel 2 [°]',
      sensDeCoupe: 'Schnittrichtung',
      profil: 'Profil',
      pasDuProfil: 'Profilteilung',
      formeFraise: 'Fräserform',
      coin: 'Ecke',
      valeurCoin: 'Eckenwert',
      coupeCentre: 'Über Mitte schneidend',
      casseCoupe: 'Schneidkantenfase',
      largeurCasseCoupe: 'Breite der Schneidkantenfase',
      angleCoupeBout: 'Stirn-Spanwinkel [°]',
      angleCoupeDiametreMin: 'Min. Umfangs-Spanwinkel (Nut 1) [°]',
      angleCoupeDiametreMax: 'Max. Umfangs-Spanwinkel (Nut 1) [°]',
      depouilleBout1: '1. Stirnfreiwinkel [°]',
      depouilleBout2: '2. Stirnfreiwinkel [°]',
      depouilleDiam1: '1. Umfangsfreiwinkel [°]',
      depouilleDiam2: '2. Umfangsfreiwinkel [°]',
      depouilleDiamType: 'Art des Umfangsfreiwinkels',
      affutageBout: 'Stirnanschliff',
      largeurAffutageBout: 'Breite des Stirnanschliffs',
      depassementCentre: 'Mittenüberdeckung',
      largeurListel: 'Führungsfasenbreite',
      largeurAme: 'Kernstegbreite',
      noyauMm: 'Kern [mm]',
      noyauArriere: 'Hinterer Kern',
      creux1: 'Hohlschliff 1 [°]',
      creux2: 'Hohlschliff 2 [°]',
      creux3: 'Hohlschliff 3 [°]',
      longueurCreux: 'Hohlschlifflänge',
      indexTaillage: 'Teilungsart',
      indexTaillageAngle: 'Teilungswinkel',
      rayonRectifiage: 'Schleifradius',
      longueurUtileMax: 'Maximale Nutzlänge LUX [mm]',
      demiAngleCone: 'Halber Kegelwinkel des Schafts BHTA [°]',
      diamCollet: 'Halsdurchmesser Nr. 1 (D2)',
      longCollet: 'Halslänge Nr. 1 (L2)',
      fabricationType: 'Fertigungsart',
      substrat: 'Substrat / Werkzeugwerkstoff',
      percentCo: 'Kobaltgehalt [%]',
      tailleGrain: 'Korngröße',
      reference: 'Referenz',
      article: 'Artikel',
    },
  },
};
